import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MatchSuggestionCard } from '@/components/reconciliation/MatchSuggestionCard';
import { ManualMatchDialog } from '@/components/reconciliation/ManualMatchDialog';
import { Building2, Landmark, Euro, Calendar, User, Home, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function Reconciliation() {
  const [manualMatchDialogOpen, setManualMatchDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const queryClient = useQueryClient();

  // Fetch pending reconciliations
  const { data: pendingReconciliations = [], isLoading: loadingPending } = useQuery({
    queryKey: ['reconciliations-pending'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transaction_reconciliations')
        .select(`
          *,
          bank_transactions (
            id,
            transaction_date,
            amount,
            description,
            bank_accounts (
              id,
              account_name,
              iban,
              scis (
                id,
                name
              )
            )
          ),
          rent_periods (
            id,
            period_start,
            period_end,
            total_amount,
            leases (
              id,
              tenants (
                id,
                first_name,
                last_name,
                tenant_type,
                company_name
              ),
              properties (
                id,
                name,
                address,
                scis (
                  id,
                  name
                )
              )
            )
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Fetch unreconciled transactions
  const { data: unreconciledTransactions = [], isLoading: loadingUnreconciled } = useQuery({
    queryKey: ['transactions-unreconciled'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bank_transactions')
        .select(`
          *,
          bank_accounts (
            id,
            account_name,
            iban,
            scis (
              id,
              name
            )
          )
        `)
        .eq('reconciliation_status', 'unreconciled')
        .gt('amount', 0)
        .order('transaction_date', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Fetch matched reconciliations
  const { data: matchedReconciliations = [], isLoading: loadingMatched } = useQuery({
    queryKey: ['reconciliations-matched'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transaction_reconciliations')
        .select(`
          *,
          bank_transactions (
            id,
            transaction_date,
            amount,
            description,
            bank_accounts (
              id,
              account_name,
              iban,
              scis (
                id,
                name
              )
            )
          ),
          rent_periods (
            id,
            period_start,
            period_end,
            total_amount,
            leases (
              id,
              tenants (
                id,
                first_name,
                last_name,
                tenant_type,
                company_name
              ),
              properties (
                id,
                name,
                address,
                scis (
                  id,
                  name
                )
              )
            )
          )
        `)
        .eq('status', 'confirmed')
        .order('confirmed_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
  });

  // Confirm reconciliation
  const confirmReconciliation = useMutation({
    mutationFn: async (reconciliationId: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // TODO: Replace with edge function call when available
      // For now, update directly
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('transaction_reconciliations')
        .update({
          status: 'confirmed',
          confirmed_by: user?.id,
          confirmed_at: new Date().toISOString(),
        })
        .eq('id', reconciliationId);

      if (error) throw error;

      // Update transaction status
      const { data: reconciliation } = await supabase
        .from('transaction_reconciliations')
        .select('bank_transaction_id')
        .eq('id', reconciliationId)
        .single();

      if (reconciliation) {
        await supabase
          .from('bank_transactions')
          .update({ reconciliation_status: 'matched' })
          .eq('id', reconciliation.bank_transaction_id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliations-pending'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliations-matched'] });
      queryClient.invalidateQueries({ queryKey: ['transactions-unreconciled'] });
      toast.success('Rapprochement confirmé');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la confirmation');
    },
  });

  // Reject reconciliation
  const rejectReconciliation = useMutation({
    mutationFn: async (reconciliationId: string) => {
      const { error } = await supabase
        .from('transaction_reconciliations')
        .update({ status: 'rejected' })
        .eq('id', reconciliationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliations-pending'] });
      queryClient.invalidateQueries({ queryKey: ['transactions-unreconciled'] });
      toast.success('Suggestion rejetée');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors du rejet');
    },
  });

  // Manual match
  const manualMatch = useMutation({
    mutationFn: async ({ transactionId, rentPeriodId }: { transactionId: string; rentPeriodId: string }) => {
      const { data: { user } } = await supabase.auth.getUser();

      // Get transaction and rent period data
      const { data: transaction } = await supabase
        .from('bank_transactions')
        .select('amount, organization_id')
        .eq('id', transactionId)
        .single();

      const { data: rentPeriod } = await supabase
        .from('rent_periods')
        .select('total_amount, lease_id')
        .eq('id', rentPeriodId)
        .single();

      if (!transaction || !rentPeriod) throw new Error('Data not found');

      // Create reconciliation
      const { error: reconciliationError } = await supabase
        .from('transaction_reconciliations')
        .insert({
          organization_id: transaction.organization_id,
          bank_transaction_id: transactionId,
          rent_period_id: rentPeriodId,
          lease_id: rentPeriod.lease_id,
          suggested_amount: rentPeriod.total_amount,
          confidence_score: 100,
          match_factors: { manual_match: true },
          status: 'confirmed',
          confirmed_by: user?.id,
          confirmed_at: new Date().toISOString(),
        });

      if (reconciliationError) throw reconciliationError;

      // Update transaction status
      await supabase
        .from('bank_transactions')
        .update({ reconciliation_status: 'matched' })
        .eq('id', transactionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reconciliations-pending'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliations-matched'] });
      queryClient.invalidateQueries({ queryKey: ['transactions-unreconciled'] });
      setManualMatchDialogOpen(false);
      setSelectedTransaction(null);
      toast.success('Rapprochement manuel créé');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors du rapprochement manuel');
    },
  });

  const handleManualMatch = (transactionId: string) => {
    const transaction = unreconciledTransactions.find(t => t.id === transactionId);
    setSelectedTransaction(transaction);
    setManualMatchDialogOpen(true);
  };

  const handleManualMatchConfirm = (rentPeriodId: string) => {
    if (selectedTransaction) {
      manualMatch.mutate({
        transactionId: selectedTransaction.id,
        rentPeriodId,
      });
    }
  };

  return (
    <AppLayout title="Rapprochement" description="Rapprochez les transactions bancaires avec les paiements">
      <PageHeader
        title="Rapprochement bancaire"
        description="Gérez les suggestions de rapprochement et créez des liens manuels"
      />

      <Tabs defaultValue="pending" className="space-y-6">
        <TabsList>
          <TabsTrigger value="pending">
            En révision ({pendingReconciliations.length})
          </TabsTrigger>
          <TabsTrigger value="unreconciled">
            Non rapprochées ({unreconciledTransactions.length})
          </TabsTrigger>
          <TabsTrigger value="matched">
            Rapprochées ({matchedReconciliations.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Review Tab */}
        <TabsContent value="pending" className="space-y-4">
          {loadingPending ? (
            <div className="text-center py-12 text-muted-foreground">
              Chargement des suggestions...
            </div>
          ) : pendingReconciliations.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle2 className="h-12 w-12 text-green-600 mb-4" />
                <p className="text-muted-foreground">
                  Aucune suggestion de rapprochement en attente
                </p>
              </CardContent>
            </Card>
          ) : (
            pendingReconciliations.map((reconciliation: any) => (
              <MatchSuggestionCard
                key={reconciliation.id}
                reconciliation={reconciliation}
                onConfirm={confirmReconciliation.mutate}
                onReject={rejectReconciliation.mutate}
                onManualMatch={handleManualMatch}
                isLoading={confirmReconciliation.isPending || rejectReconciliation.isPending}
              />
            ))
          )}
        </TabsContent>

        {/* Unreconciled Tab */}
        <TabsContent value="unreconciled" className="space-y-4">
          {loadingUnreconciled ? (
            <div className="text-center py-12 text-muted-foreground">
              Chargement des transactions...
            </div>
          ) : unreconciledTransactions.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle2 className="h-12 w-12 text-green-600 mb-4" />
                <p className="text-muted-foreground">
                  Toutes les transactions sont rapprochées
                </p>
              </CardContent>
            </Card>
          ) : (
            unreconciledTransactions.map((transaction: any) => (
              <Card key={transaction.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-3 flex-1">
                      {/* Transaction Header */}
                      <div className="flex items-center gap-3 text-sm">
                        <div className="flex items-center gap-1 font-bold">
                          <Building2 className="h-4 w-4 text-primary" />
                          {transaction.bank_accounts?.scis?.name}
                        </div>
                        <div className="text-muted-foreground">|</div>
                        <div className="flex items-center gap-1">
                          <Landmark className="h-4 w-4 text-muted-foreground" />
                          {transaction.bank_accounts?.account_name}
                          {transaction.bank_accounts?.iban &&
                            ` (...${transaction.bank_accounts.iban.slice(-4)})`
                          }
                        </div>
                      </div>

                      {/* Transaction Details */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1 text-2xl font-bold text-green-600">
                          <Euro className="h-5 w-5" />
                          +{transaction.amount?.toFixed(2)} €
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {format(new Date(transaction.transaction_date), 'dd/MM/yyyy', { locale: fr })}
                        </div>
                      </div>

                      {transaction.description && (
                        <p className="text-sm text-muted-foreground">{transaction.description}</p>
                      )}

                      <div className="pt-2">
                        <button
                          onClick={() => handleManualMatch(transaction.id)}
                          className="text-primary hover:underline text-sm font-medium"
                        >
                          Rapprocher manuellement
                        </button>
                      </div>
                    </div>

                    <Badge variant="outline" className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Non rapprochée
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Matched Tab */}
        <TabsContent value="matched" className="space-y-4">
          {loadingMatched ? (
            <div className="text-center py-12 text-muted-foreground">
              Chargement des rapprochements...
            </div>
          ) : matchedReconciliations.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Aucun rapprochement confirmé
                </p>
              </CardContent>
            </Card>
          ) : (
            matchedReconciliations.map((reconciliation: any) => {
              const transaction = reconciliation.bank_transactions;
              const rentPeriod = reconciliation.rent_periods;
              const lease = rentPeriod?.leases;
              const tenant = lease?.tenants;
              const property = lease?.properties;

              const tenantName = tenant?.tenant_type === 'professional' && tenant?.company_name
                ? tenant.company_name
                : `${tenant?.first_name} ${tenant?.last_name}`;

              return (
                <Card key={reconciliation.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="space-y-3 flex-1">
                        {/* Transaction Header */}
                        <div className="flex items-center gap-3 text-sm">
                          <div className="flex items-center gap-1 font-bold">
                            <Building2 className="h-4 w-4 text-primary" />
                            {transaction?.bank_accounts?.scis?.name}
                          </div>
                          <div className="text-muted-foreground">|</div>
                          <div className="flex items-center gap-1">
                            <Landmark className="h-4 w-4 text-muted-foreground" />
                            {transaction?.bank_accounts?.account_name}
                          </div>
                        </div>

                        {/* Transaction & Match Info */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Transaction</p>
                            <div className="flex items-center gap-1 text-lg font-bold text-green-600">
                              <Euro className="h-4 w-4" />
                              +{transaction?.amount?.toFixed(2)} €
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(transaction?.transaction_date), 'dd/MM/yyyy', { locale: fr })}
                            </div>
                          </div>

                          <div>
                            <p className="text-xs text-muted-foreground">Rapproché avec</p>
                            <div className="flex items-center gap-1 text-sm font-medium">
                              <User className="h-3 w-3" />
                              {tenantName}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Home className="h-3 w-3" />
                              {property?.name || property?.address}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(rentPeriod?.period_start), 'MMMM yyyy', { locale: fr })}
                            </div>
                          </div>
                        </div>

                        {reconciliation.confirmed_at && (
                          <p className="text-xs text-muted-foreground">
                            Confirmé le {format(new Date(reconciliation.confirmed_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                          </p>
                        )}
                      </div>

                      <Badge className="bg-green-600">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Rapprochée
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>

      {/* Manual Match Dialog */}
      <ManualMatchDialog
        open={manualMatchDialogOpen}
        onOpenChange={setManualMatchDialogOpen}
        transaction={selectedTransaction}
        onMatch={handleManualMatchConfirm}
        isLoading={manualMatch.isPending}
      />
    </AppLayout>
  );
}
